import React, { useState, useEffect, useMemo } from "react";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";

import DeleteIcon from "@material-ui/icons/Delete";

import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import { endPoint } from "utils/config";
import { assetUrl, readableError } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * Every field on this mutation is non-null, which is why the form
 * validates all of them before submitting: the previous two-step wizard
 * let you reach step two with step one incomplete and only found out at
 * the end.
 * ------------------------------------------------------------------ */

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

const CREATE_VERIFICATION = gql`
  mutation createDriverVerificationRequestByAdmin(
    $driverId: ID!
    $gender: DriverGender!
    $birthDate: Date!
    $address: DriverAddressByAdminInput!
    $profileImageUrl: String!
    $drivingLicence: DriverDrivingLicenceByAdminInput!
    $drivingRecordPhotoUrl: String!
    $canadianVerificationPhotoUrl: String!
    $canadianVerificationExpireDate: Date!
    $backgroundCheckDocumentPhotoUrl: String!
  ) {
    createDriverVerificationRequestByAdmin(
      driverId: $driverId
      input: {
        gender: $gender
        birthDate: $birthDate
        address: $address
        profileImageUrl: $profileImageUrl
        drivingLicence: $drivingLicence
        drivingRecordPhotoUrl: $drivingRecordPhotoUrl
        canadianVerificationPhotoUrl: $canadianVerificationPhotoUrl
        canadianVerificationExpireDate: $canadianVerificationExpireDate
        backgroundCheckDocumentPhotoUrl: $backgroundCheckDocumentPhotoUrl
      }
    ) {
      _id
      fullName
    }
  }
`;

const GET_DRIVERS = gql`
  query getDriversForVerificationForm($fullName: String) {
    getDriversByAdmin(pagination: { limit: 20, skip: 0 }, filters: { fullName: $fullName }) {
      _id
      fullName
      phoneNumber
    }
  }
`;

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
];
const GENDER_LABELS = { MALE: "Male", FEMALE: "Female" };

const SEARCH_DEBOUNCE_MS = 350;

/**
 * AddDriver - submit a verification request on a driver's behalf.
 *
 * Replaces a two-step wizard (Step1/Step2) whose only reason to be split
 * was length. The mutation is atomic, so one scrollable form lets the
 * whole submission be reviewed before it is sent, and every required
 * document is visible at once rather than hidden behind a Next button.
 */
const AddDriver = () => {
  const [driverId, setDriverId] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [gender, setGender] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [addressFull, setAddressFull] = useState("");
  const [addressZip, setAddressZip] = useState("");

  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [licenceId, setLicenceId] = useState("");
  const [licencePhotoUrl, setLicencePhotoUrl] = useState("");
  const [licenceExpireDate, setLicenceExpireDate] = useState("");
  const [drivingRecordPhotoUrl, setDrivingRecordPhotoUrl] = useState("");
  const [canadianVerificationPhotoUrl, setCanadianVerificationPhotoUrl] = useState("");
  const [canadianVerificationExpireDate, setCanadianVerificationExpireDate] = useState("");
  const [backgroundCheckDocumentPhotoUrl, setBackgroundCheckDocumentPhotoUrl] = useState("");

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const [findDrivers, { data: driverData }] = useLazyQuery(GET_DRIVERS, {
    fetchPolicy: "network-only",
  });
  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [createVerification] = useMutation(CREATE_VERIFICATION);

  useEffect(() => {
    const handler = setTimeout(() => {
      findDrivers({ variables: { fullName: driverSearch.trim() } });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [driverSearch, findDrivers]);

  const driverOptions = useMemo(
    () =>
      ((driverData && driverData.getDriversByAdmin) || []).map((d) => ({
        value: d._id,
        label: d.phoneNumber ? `${d.fullName} · ${d.phoneNumber}` : d.fullName,
      })),
    [driverData]
  );
  const selectedDriverLabel = (driverOptions.find((o) => o.value === driverId) || {}).label;

  /* ---------------- uploads ---------------- */

  const onPickFile = (event, slot, setter) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploading(slot);
    setFormError("");
    uploadFile({ variables: { file, folderName: "drivers" } })
      .then((res) => {
        setUploading("");
        setter(res.data.uploadFile.url);
        setErrors((current) => ({ ...current, [slot]: undefined }));
      })
      .catch((err) => {
        setUploading("");
        setFormError(readableError(err));
      });
    event.target.value = "";
  };

  // A plain function rather than a component declared in the render body,
  // so React does not rebuild the file input every time `uploading`
  // changes.
  const documentField = ({ label, slot, url, setter, hint }) => (
    <Field label={label} hint={hint} error={errors[slot]} required key={slot}>
      <div className="superPersonImages">
        {url && (
          <span className="superPersonImageButton" style={{ position: "relative" }}>
            <img src={assetUrl(url)} alt="" />
            <IconButton
              size="small"
              className="rowActionButton"
              onClick={() => setter("")}
              title="Remove"
              style={{ position: "absolute", top: 2, right: 2, background: "rgba(255,255,255,0.9)" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        )}
        <label className="superPersonFilePicker">
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => onPickFile(e, slot, setter)}
          />
          {uploading === slot ? "Uploading…" : url ? "Replace" : "Choose a file"}
        </label>
      </div>
    </Field>
  );

  /* ---------------- submit ---------------- */

  const validate = () => {
    const next = {};
    if (!driverId) next.driver = "Choose the driver this is for.";
    if (!gender) next.gender = "Required.";
    if (!birthDate) next.birthDate = "Required.";
    if (!addressFull.trim()) next.addressFull = "Required.";
    if (!addressZip.trim()) next.addressZip = "Required.";
    if (!profileImageUrl) next.profileImage = "A profile photo is required.";
    if (!licenceId.trim()) next.licenceId = "Required.";
    if (!licencePhotoUrl) next.licencePhoto = "A photo of the licence is required.";
    if (!licenceExpireDate) next.licenceExpireDate = "Required.";
    else if (moment(licenceExpireDate).isBefore(moment())) {
      next.licenceExpireDate = "This licence has already expired.";
    }
    if (!drivingRecordPhotoUrl) next.drivingRecord = "A driving record is required.";
    if (!canadianVerificationPhotoUrl) next.workPermit = "A work eligibility document is required.";
    if (!canadianVerificationExpireDate) next.workPermitExpiry = "Required.";
    else if (moment(canadianVerificationExpireDate).isBefore(moment())) {
      next.workPermitExpiry = "This document has already expired.";
    }
    if (!backgroundCheckDocumentPhotoUrl) next.backgroundCheck = "A background check is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving || uploading) return;
    setFormError("");
    if (!validate()) return;

    setSaving(true);
    createVerification({
      variables: {
        driverId,
        gender,
        birthDate,
        address: { full: addressFull.trim(), zipCode: addressZip.trim() },
        profileImageUrl,
        drivingLicence: {
          licenceId: licenceId.trim(),
          photoUrl: licencePhotoUrl,
          expireDate: licenceExpireDate,
        },
        drivingRecordPhotoUrl,
        canadianVerificationPhotoUrl,
        canadianVerificationExpireDate,
        backgroundCheckDocumentPhotoUrl,
      },
    })
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/verifications`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  return (
    <div className="page superPeoplePage">
      <main className="main">
        <PageHeader
          title="Submit driver verification"
          description="Send a verification request on a driver's behalf. It lands in the verification queue for a decision."
        />

        <form onSubmit={onSubmit}>
          <Panel title="Driver">
            <div className="superPersonForm">
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <Field label="Find a driver" error={errors.driver} required>
                <SearchField
                  placeholder="Search drivers by name..."
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                />
              </Field>
              <FilterDropdown
                label={selectedDriverLabel || "Choose a driver"}
                options={
                  driverOptions.length
                    ? driverOptions
                    : [{ value: "", label: "No matches — try another name" }]
                }
                value={driverId}
                onChange={(e) => setDriverId(e.target.value)}
              />

              <div className="superPersonFormRow">
                <Field label="Gender" error={errors.gender} required>
                  <FilterDropdown
                    label={gender ? GENDER_LABELS[gender] : "Choose"}
                    options={GENDER_OPTIONS}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  />
                </Field>

                <Field label="Date of birth" htmlFor="birthDate" error={errors.birthDate} required>
                  <input
                    id="birthDate"
                    className="dsInput"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </Field>
              </div>

              <div className="superPersonFormRow">
                <Field label="Address" htmlFor="addressFull" error={errors.addressFull} required>
                  <input
                    id="addressFull"
                    className="dsInput"
                    type="text"
                    value={addressFull}
                    onChange={(e) => setAddressFull(e.target.value)}
                  />
                </Field>

                <Field label="Postal code" htmlFor="addressZip" error={errors.addressZip} required>
                  <input
                    id="addressZip"
                    className="dsInput"
                    type="text"
                    value={addressZip}
                    onChange={(e) => setAddressZip(e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </Panel>

          <Panel title="Driving licence">
            <div className="superPersonForm">
              <div className="superPersonFormRow">
                <Field label="Licence number" htmlFor="licenceId" error={errors.licenceId} required>
                  <input
                    id="licenceId"
                    className="dsInput"
                    type="text"
                    value={licenceId}
                    onChange={(e) => setLicenceId(e.target.value)}
                  />
                </Field>

                <Field
                  label="Licence expires"
                  htmlFor="licenceExpireDate"
                  error={errors.licenceExpireDate}
                  required
                >
                  <input
                    id="licenceExpireDate"
                    className="dsInput"
                    type="date"
                    value={licenceExpireDate}
                    onChange={(e) => setLicenceExpireDate(e.target.value)}
                  />
                </Field>
              </div>

              {documentField({
                label: "Licence photo",
                slot: "licencePhoto",
                url: licencePhotoUrl,
                setter: setLicencePhotoUrl,
              })}
            </div>
          </Panel>

          <Panel title="Documents" subtitle="All of these are required before a decision can be made.">
            <div className="superPersonForm">
              {documentField({
                label: "Profile photo",
                slot: "profileImage",
                url: profileImageUrl,
                setter: setProfileImageUrl,
                hint: "Shown to passengers when the driver accepts a trip.",
              })}

              {documentField({
                label: "Driving record",
                slot: "drivingRecord",
                url: drivingRecordPhotoUrl,
                setter: setDrivingRecordPhotoUrl,
              })}

              {documentField({
                label: "Work eligibility",
                slot: "workPermit",
                url: canadianVerificationPhotoUrl,
                setter: setCanadianVerificationPhotoUrl,
              })}

              <Field
                label="Work eligibility expires"
                htmlFor="workPermitExpiry"
                error={errors.workPermitExpiry}
                required
              >
                <input
                  id="workPermitExpiry"
                  className="dsInput"
                  type="date"
                  value={canadianVerificationExpireDate}
                  onChange={(e) => setCanadianVerificationExpireDate(e.target.value)}
                />
              </Field>

              {documentField({
                label: "Background check",
                slot: "backgroundCheck",
                url: backgroundCheckDocumentPhotoUrl,
                setter: setBackgroundCheckDocumentPhotoUrl,
              })}
            </div>
          </Panel>

          <div className="superPersonFooter">
            <Button
              variant="secondary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/drivers`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving} disabled={Boolean(uploading)}>
              Submit for review
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AddDriver;
